import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { type IPublicClientApplication } from '@azure/msal-browser';
import { createAppQueryClient } from '@/lib/query-client';
import { prepareBrowserAuth } from '@/lib/auth-bootstrap';

import { App } from '@/App';
import { AuthProvider } from '@/components/auth/auth-provider';
import { UserProvider } from '@/components/auth/user-provider';
import { TenantEntityProvider } from '@/components/access/tenant-entity-provider';
import { BrandingProvider } from '@/lib/branding/branding-provider';
import { ThemeProvider } from '@/components/theme/theme-provider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { WorkspaceProvider } from '@/components/workspace/workspace-provider';
import { type AuthProviderKind } from '@/lib/auth-config';
import { isMsalAuthTransportFrame } from '@/lib/embed';
import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@/index.css';

const queryClient = createAppQueryClient();

function renderApplication(
  instance: IPublicClientApplication | null,
  provider: AuthProviderKind = 'workforce'
) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TooltipProvider>
            <BrowserRouter>
              <AuthProvider instance={instance} provider={provider}>
                <UserProvider>
                  <TenantEntityProvider>
                    <BrandingProvider>
                      <WorkspaceProvider>
                        <App />
                      </WorkspaceProvider>
                    </BrandingProvider>
                  </TenantEntityProvider>
                </UserProvider>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}

function renderBootstrapError() {
  createRoot(document.getElementById('root')!).render(
    <div className="text-muted-foreground flex min-h-svh items-center justify-center p-6 text-sm">
      Microsoft sign-in could not be prepared. Open the application again to retry.
    </div>
  );
}

async function bootstrap() {
  const { instance, provider } = await prepareBrowserAuth();
  renderApplication(instance, provider);
}

// Hidden silent-auth frames and MSAL popup callbacks deliberately remain
// dormant. A normal CDF preview iframe does not match this narrow guard.
if (!isMsalAuthTransportFrame()) {
  void bootstrap().catch(() => {
    console.error('[auth] Microsoft sign-in initialization failed.');
    renderBootstrapError();
  });
}
